import {
  Badge,
  Box,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useEffect, useState } from "react";
import classes from "./PrintView.module.css";
import { findSingleAnalysisRun } from "../monitorsApi";
import LlmProviderLogo from "../../LlmProviders/LlmProviderLogo";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BarChart } from "@mantine/charts";
import { fetchBrandAnalyticsTimeline } from "../brandsApi";
import { IconInfoCircle } from "@tabler/icons-react";
import { useBrandsStore } from "../brandsStore";

import brandkarma from "../../brandkarma.png";
import BrandKarmaLogo from "../../_Shared/BrandKarmaLogo";
import BrandLogo from "../../_Shared/BrandLogo";
import StatsOverview from "../AnalysisResults/StatsOverview";

function DistributionChart({ brandId }) {
  {
    const [timelineData, setTimelineData] = useState([]);

    useEffect(() => {
      fetchBrandAnalyticsTimeline(brandId, {}).then((data) => {
        setTimelineData(data.timePoints);
      });
    }, [brandId]);

    if (timelineData?.length === 0) return null;

    return (
      <SimpleGrid cols={1} spacing="md">
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Latest Distribution Analysis
          </Title>

          {timelineData[timelineData.length - 1]?.analytics && (
            <SimpleGrid cols={3} spacing="lg">
              {/* Sentiment Distribution */}
              <Box>
                <Text fw={600} mb="xs">
                  Sentiment Score Distribution
                  <Tooltip
                    w="35%"
                    multiline
                    label="The distribution of sentiment scores across mentions. A higher score indicates more positive sentiment. The height of the bars is the total count of mentions with that sentiment score range. You want to see high bars on the right side of the chart (positive sentiments) and ideally none on the left (negative sentiments)."
                  >
                    <IconInfoCircle size={16} />
                  </Tooltip>
                </Text>
                <BarChart
                  h={200}
                  data={
                    timelineData[
                      timelineData.length - 1
                    ].analytics.sentimentScoreDistribution?.map((bucket) => ({
                      // range: /*bucket.label || */ `${bucket.rangeStart.toFixed(
                      //   1
                      // )}-${bucket.rangeEnd.toFixed(1)}`,
                      range:
                        bucket.rangeStart < 0
                          ? `< ${bucket.rangeEnd.toFixed(1)}`
                          : `> ${bucket.rangeStart.toFixed(1)}`,
                      count: bucket.count,
                    })) || []
                  }
                  dataKey="range"
                  series={[{ name: "count", color: "green.6" }]}
                />
              </Box>

              {/* Share of Voice Distribution */}
              <Box>
                <Text fw={600} mb="xs">
                  Share of Voice Distribution
                  <Tooltip
                    w="35%"
                    multiline
                    label="The distribution of share of voice across mentions. A higher percentage indicates a greater share of voice. The height of the bars is the total count of mentions within that share of voice range. You want to see high bars on the right side of the chart (higher share of voice). An overly high share of voice (e.g., > 50%) may indicate a brand is dominating the conversation, which can be an indicator of too narrow prompts."
                  >
                    <IconInfoCircle size={16} />
                  </Tooltip>
                </Text>
                <BarChart
                  h={200}
                  data={
                    timelineData[
                      timelineData.length - 1
                    ].analytics.shareOfVoiceDistribution?.map((bucket) => ({
                      //range: /*bucket.label ||*/ `${bucket.rangeStart.toFixed(0)}-${bucket.rangeEnd.toFixed(0)}`,
                      range: `≤ ${bucket.rangeEnd.toFixed(0)}%`,
                      count: bucket.count,
                    })) || []
                  }
                  dataKey="range"
                  series={[{ name: "count", color: "blue.6" }]}
                />
              </Box>

              {/* Mention Rank Distribution */}
              <Box>
                <Text fw={600} mb="xs">
                  Mention Rank Distribution
                  <Tooltip
                    w="35%"
                    multiline
                    label="The distribution of mention ranks across mentions. A lower rank indicates a more prominent mention. The height of the bars is the total count of mentions within that rank. You want to see high bars on the left side of the chart (lower ranks means you get mentioned earlier)."
                  >
                    <IconInfoCircle size={16} />
                  </Tooltip>
                </Text>
                <BarChart
                  h={200}
                  data={
                    timelineData[
                      timelineData.length - 1
                    ].analytics.mentionRankDistribution?.map((bucket) => ({
                      //range: /*bucket.label ||*/ `${bucket.rangeStart.toFixed(0)}-${bucket.rangeEnd.toFixed(0)}`,
                      range: `${bucket.rangeStart.toFixed(0)}`,
                      count: bucket.count,
                    })) || []
                  }
                  dataKey="range"
                  series={[{ name: "count", color: "orange.6" }]}
                />
              </Box>
            </SimpleGrid>
          )}
        </Paper>
      </SimpleGrid>
    );
  }
}

export default function PrintView({ params }) {
  const [analysisRun, setAnalysisRun] = useState(null);
  const [brand, setBrand] = useState(null);

  const { getBrandById } = useBrandsStore();

  const brandId = new URLSearchParams(window.location.search).get("brandId");
  const reportId = params.reportId;

  const reloadAnalysisRun = async () => {
    const run = await findSingleAnalysisRun(brandId, reportId);
    setAnalysisRun(run);
  };

  useEffect(() => {
    if (!brandId || !reportId) return;
    reloadAnalysisRun();
    getBrandById(brandId).then((brandData) => {
      setBrand(brandData);
    });
  }, [brandId, reportId]);

  useEffect(() => {
    if (brand && analysisRun) {
      const dateStr = new Date(analysisRun.endTimeUtc)
        .toISOString()
        .slice(0, 10);

      document.title = `BrandKarma - ${brand.name} AI Visibility Report - ${dateStr}`;
    }
  }, [brand, analysisRun]);

  const metaAnalysis = analysisRun?.analyses?.find(
    (a) => a.providerIds?.length > 0
  );

  if (!brand || !analysisRun) {
    console.log("Brand or analysis run not loaded yet");
    console.log("Brand:", brand);
    console.log("Analysis Run:", analysisRun);
    return <Loader />;
  }

  return (
    <Container>
      {analysisRun && (
        <Paper key={analysisRun.id} shadow="none" p="md" mb="md">
          <Group justify="flex-end" mt="sm" mb="lg">
            <BrandKarmaLogo size={28} color="#0f172a" />
            <img src={brandkarma} alt="BrandKarma" height={28} />
          </Group>

          <Group>
            <BrandLogo brand={brand} />
            <Title>{brand.name} AI Visibility Report </Title>
          </Group>

          <Group justify="space-between" align="center">
            <Stack w="100%">
              <Group justify="space-between" align="center">
                <Text fz="lg" fw={700}>
                  {analysisRun.totalQueriesFinished} Queries
                </Text>

                {/* <Text fz="sm" c="dimmed">
                  {new Date(analysisRun.startTimeUtc).toLocaleString()} &mdash;{" "}
                  {new Date(analysisRun.endTimeUtc).toLocaleString()}
                </Text> */}

                <Text fz="sm" c="dimmed">
                  {new Date(analysisRun.endTimeUtc).toLocaleString()}
                </Text>

                <Group>
                  {metaAnalysis.providerIds?.map((providerId) => (
                    <LlmProviderLogo
                      key={providerId}
                      llmProvider={providerId}
                      size={32}
                    />
                  ))}
                </Group>
              </Group>
            </Stack>
          </Group>
          <Divider my="md" />
          <Stack className={classes.pagebreak} mt="md">
            <Title order={2}>Overview / All LLMs</Title>
            <Text>
              This is a comprehensive summary taking into account all LLMs that
              were used in the analysis. See the following pages for analyses of
              individual LLMs.
            </Text>
            <StatsOverview stats={metaAnalysis.stats} />
            <DistributionChart brandId={brandId} />

            <Divider my="md" />
            <div className={classes.md}>
              <Markdown remarkPlugins={[remarkGfm]}>{metaAnalysis.analysis}</Markdown>
            </div>
            {metaAnalysis.recommendations && (
              <>
                <Divider my="md" />
                <Title order={3}>Optimization Recommendations</Title>
                <div className={classes.md}>
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {metaAnalysis.recommendations}
                  </Markdown>
                </div>
              </>
            )}
          </Stack>
          {analysisRun.analyses
            ?.filter((p) => p.status != "superseded" && p.providerId)
            .map((analysis) => (
              <Stack className={classes.pagebreak} key={analysis.id} mt="md">
                <Group justify="space-between" align="center">
                  <Group>
                    {analysis.providerId ? (
                      <LlmProviderLogo
                        showName
                        llmProvider={analysis.providerId}
                        size={32}
                      />
                    ) : (
                      analysis.providerIds?.map((providerId) => (
                        <LlmProviderLogo
                          key={providerId}
                          llmProvider={providerId}
                          size={32}
                        />
                      ))
                    )}
                  </Group>
                  {analysis.providerId ? (
                    <Text fw={600} fz="lg">
                      {analysis.totalQueries} Queries
                    </Text>
                  ) : (
                    <>
                      <Badge>Executive Summary</Badge>
                    </>
                  )}
                </Group>
                <StatsOverview stats={analysis.stats} />

                <Divider my="md" />
                <div className={classes.md}>
                  <Markdown remarkPlugins={[remarkGfm]}>{analysis.analysis}</Markdown>
                </div>
              </Stack>
            ))}
          <Divider my="md" />

          <Box mt="xl">
            <Title mb="md" order={3}>
              Glossary
            </Title>
            <SimpleGrid cols={2}>
              <Paper p="md" shadow="none" withBorder>
                <Text fw={500} mb="xs">
                  Average Rank
                </Text>
                <Text fz="sm">
                  The average position of your brand mentions, calculated across
                  all relevant queries. A lower average rank indicates better
                  visibility and prominence in the search results. Your average
                  rank is 1.0 if your brand always gets mentioned first.
                </Text>
              </Paper>
              <Paper p="md" shadow="none" withBorder>
                <Text fw={500} mb="xs">
                  Share of Voice (Unaided)
                </Text>
                <Text fz="sm">
                  The number of times your brand was mentioned in unaided
                  queries (i.e., without mentioning our brand name or other
                  hints) divided by the number of all brand mentions. If only
                  your brand was mentioned, your share-of-voice is 100%. If your
                  brand wasn't mentioned at all, it's 0%.
                </Text>
              </Paper>

              <Paper p="md" shadow="none" withBorder>
                <Text fw={500} mb="xs">
                  Share of Voice (Aided)
                </Text>
                <Text fz="sm">
                  The number of times your brand was mentioned in aided queries,
                  divided by the number of all brand mentions. If only your
                  brand was mentioned, your share-of-voice is 100%. If your
                  brand doesn't get mentioned, it's 0%.
                </Text>
              </Paper>

              <Paper p="md" shadow="none" withBorder>
                <Text fw={500} mb="xs">
                  Average Reputation
                </Text>
                <Text fz="sm">
                  The average sentiment score of your brand mentions, calculated
                  across all relevant queries. A higher average reputation
                  indicates a more positive perception of your brand in the
                  search results. Your average reputation is 1 if all mentions
                  are positive, and -1 if all mentions are negative.
                </Text>
              </Paper>

              <Paper p="md" shadow="none" withBorder>
                <Text fw={500} mb="xs">
                  Distributions
                </Text>
                <Text fz="sm">
                  Distributions show how often certain ranges of sentiment,
                  share-of-voice and ranks occur within your brand mentions.
                  This avoids confusion with averages; for example, a very
                  divided reputation would have a 'neutral' average, but that
                  doesn't tell the full story. Distributions help understand the
                  overall landscape of your brand's performance and identify
                  areas for improvement.
                </Text>
              </Paper>
            </SimpleGrid>
          </Box>
        </Paper>
      )}
    </Container>
  );
}
